import {
  Panel,
  tokens,
  Typography,
  Stack,
  MetadataChipV2,
} from '@cast/design-system';
import type { ScalingPolicyAssignmentRule } from '@cast/types';

import type { WrappedPolicy } from '../../../open-api-types/autopilot-types';

type ProposedPolicyAssignmentRuleProps = {
  policy: WrappedPolicy;
};

const parseGvkKind = (gvk: string): string => {
  const parts = gvk.split('/');
  return parts[parts.length - 1] ?? gvk;
};

const formatRuleScope = (rule: ScalingPolicyAssignmentRule): string => {
  if (rule.workload?.gvk?.length) {
    const kinds = rule.workload.gvk.map(parseGvkKind).join(' / ');
    return `${kinds} — exact match`;
  }
  return 'exact match';
};

const AssignmentRuleRow = ({
  rule,
  index,
}: {
  rule: ScalingPolicyAssignmentRule;
  index: number;
}) => {
  const namespaces = rule.namespace?.names ?? [];
  const scope = formatRuleScope(rule);

  return (
    <Stack direction="row" gap={12} alignItems="flex-start">
      <Typography
        variant="L10M"
        sx={{
          color: tokens.theme.fg.structural.secondary,
          whiteSpace: 'nowrap',
          pt: '2px',
        }}
      >
        RULE {index + 1}
      </Typography>
      <Stack gap={4}>
        {namespaces.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={4} alignItems="center">
            <Typography
              variant="P12M"
              sx={{ color: tokens.theme.fg.structural.primary }}
            >
              Namespace:
            </Typography>
            {namespaces.map((ns) => (
              <MetadataChipV2 key={ns} label={ns} size="xs" variant="strong" />
            ))}
          </Stack>
        )}
        <Typography
          variant="P12M"
          sx={{ color: tokens.theme.fg.structural.primary }}
        >
          {scope}
        </Typography>
      </Stack>
    </Stack>
  );
};

export const ProposedPolicyAssignmentRule = ({
  policy,
}: ProposedPolicyAssignmentRuleProps) => {
  const rules = policy.payload.assignmentRules;

  if (!rules?.length) {
    return null;
  }

  return (
    <Panel
      sx={{ p: 16 }}
      data-testid={`policy-assignment-rule-${policy.payload.name}`}
    >
      <Typography
        variant="P12B"
        sx={{
          mb: 12,
          display: 'block',
          color: tokens.theme.fg.structural.primary,
        }}
      >
        Assignment rule
      </Typography>
      <Stack gap={12}>
        {rules.map((rule, i) => (
          <AssignmentRuleRow key={i} rule={rule} index={i} />
        ))}
      </Stack>
    </Panel>
  );
};
